/**
 * Ownership and documents on a customer's page (staff directors and admins): the customer's ownership register
 * (saved under RLS and audited; the database refuses secret-looking values), the handover pack, and documents shared
 * with the customer (uploaded to private R2 storage through the API, by category).
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { Icon } from "../components/Icon";
import {
  deleteEntry,
  openDocument,
  openHandoverPack,
  removeDocument,
  saveEntry,
  uploadDocument,
  type CustomerDetail,
  type DocumentCategory,
  type EntryFields,
  type OwnershipKind,
  type StaffOwnershipEntry,
} from "../lib/customers-data";
import { fmtDate, fmtPlainDate } from "../lib/format";
import { secretProblem } from "../lib/secrets";

type Act = (fn: () => Promise<unknown>, done?: string) => Promise<void>;

export const KIND_LABEL: Record<OwnershipKind, string> = {
  domain: "Domain",
  cloudflare: "Cloudflare account",
  database: "Database",
  repository: "Code repository",
  mailbox: "Mailbox",
  payments: "Payments",
  accounting: "Accounting",
  other: "Other account",
};
const KINDS = Object.keys(KIND_LABEL) as OwnershipKind[];
export const CATEGORY_LABEL: Record<DocumentCategory, string> = {
  agreement: "Agreement (everyone at the customer)",
  financial: "Financial (owners and accounts)",
  report: "Report (owners and members)",
  technical: "Technical (owners and members)",
  other: "Other (owners and members)",
};
const CATEGORIES = Object.keys(CATEGORY_LABEL) as DocumentCategory[];

function Field({ label, children, wide, hint }: { label: string; children: ReactNode; wide?: boolean; hint?: string | null }) {
  return (
    <label className={`field${wide ? " cs-wide" : ""}`}>
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__error" role="alert">{hint}</span>}
    </label>
  );
}

/* ------------------------------------------------------------------ ownership register */

export function CustomerOwnershipCard({ data, act }: { data: CustomerDetail; act: Act }) {
  const [editing, setEditing] = useState<string | null>(null);
  if (!__OPS_PORTAL__.customerOwnership) return null;
  const cid = data.customer.id;
  const { entries, packs } = data.ownership;
  return (
    <section className="card" aria-labelledby="ownership-title">
      <div className="card__head">
        <h2 className="card__title" id="ownership-title">Ownership register</h2>
        <button type="button" className="btn btn--outline btn--xs" onClick={() => act(() => openHandoverPack(cid))}>
          <Icon name="download" size={16} /> Handover pack
        </button>
      </div>
      <div className="card__body stack">
        <p className="cs-secret-note">
          <Icon name="lock" size={18} />
          <span><strong>Never enter passwords, PINs, recovery codes, API keys or tokens.</strong> Only the account name or ID, who the admin is, and how they get in. The database refuses values that look like a secret.</span>
        </p>
        {entries.length === 0 ? (
          <p className="muted">Nothing yet. Add each thing the customer owns: their domain, Cloudflare account, database, code repository, mailbox, and payment and accounting accounts. They see it under Ownership, and in their handover pack.</p>
        ) : (
          <ul className="list">
            {entries.map((e) => (
              <li key={e.id} className="list__row cs-host">
                {editing === e.id ? (
                  <EntryForm customerId={cid} entry={e} act={act} onDone={() => setEditing(null)} />
                ) : (
                  <>
                    <div className="cs-host__main">
                      <span><strong>{e.label}</strong> <span className="chip chip--outline cs-gap">{KIND_LABEL[e.kind]}</span></span>
                      <span className="sub">{[e.provider, e.account_ref, e.admin_name && `Admin: ${e.admin_name}`, e.renews_on && `Renews ${fmtPlainDate(e.renews_on)}`, e.region].filter(Boolean).join(". ")}</span>
                      {e.access_note && <span className="sub">How they get in: {e.access_note}</span>}
                    </div>
                    <span className="cs-login__actions">
                      <button type="button" className="btn btn--outline btn--xs" onClick={() => setEditing(e.id)}>Edit</button>
                      <button type="button" className="btn btn--outline btn--xs" onClick={() => window.confirm(`Remove ${e.label} from the register?`) && act(() => deleteEntry(e.id), "Removed from the register.")}>Remove</button>
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        <details className="cs-details cs-details--inner">
          <summary className="cs-summary"><Icon name="plus" size={18} /> Add to the register</summary>
          <EntryForm key={entries.length} customerId={cid} entry={null} act={act} position={entries.length + 1} />
        </details>
        {packs.length > 0 && (
          <p className="small muted">
            Handover packs made: {packs.slice(0, 3).map((p, i) => (
              <span key={p.id}>{i > 0 && ", "}<button type="button" className="link-btn" onClick={() => act(() => openHandoverPack(cid, p.id))}>{fmtDate(p.created_at)}</button> by {p.made_by_name}</span>
            ))}.
          </p>
        )}
      </div>
    </section>
  );
}

const blank = (s: string) => (s.trim() ? s.trim() : null);

function EntryForm({ customerId, entry, act, onDone, position }: { customerId: string; entry: StaffOwnershipEntry | null; act: Act; onDone?: () => void; position?: number }) {
  const [f, setF] = useState({
    kind: entry?.kind ?? ("domain" as OwnershipKind),
    label: entry?.label ?? "",
    provider: entry?.provider ?? "",
    account_ref: entry?.account_ref ?? "",
    admin_name: entry?.admin_name ?? "",
    renews_on: entry?.renews_on ?? "",
    region: entry?.region ?? "",
    access_note: entry?.access_note ?? "",
    position: String(entry?.position ?? position ?? 1),
  });
  const problem = (v: string) => secretProblem(v) && `Not saved: ${secretProblem(v)}.`;
  const bad = [f.label, f.provider, f.account_ref, f.admin_name, f.region, f.access_note].some((v) => secretProblem(v));
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (bad) return;
    const fields: EntryFields = {
      kind: f.kind,
      label: f.label.trim(),
      provider: blank(f.provider),
      account_ref: blank(f.account_ref),
      admin_name: blank(f.admin_name),
      renews_on: f.renews_on || null,
      region: blank(f.region),
      access_note: blank(f.access_note),
      position: Number(f.position) || 0,
    };
    await act(() => saveEntry(customerId, entry?.id ?? null, fields), entry ? "Register saved." : "Added to the register.");
    onDone?.();
  }
  return (
    <form className="card__body stack cs-entry-form" onSubmit={submit}>
      <div className="cs-grid">
        <Field label="Kind">
          <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as OwnershipKind })}>
            {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
        </Field>
        <Field label="Label (shown to the customer)" hint={problem(f.label)}><input className="input" required maxLength={120} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Main domain" /></Field>
        <Field label="Provider" hint={problem(f.provider)}><input className="input" maxLength={120} value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} placeholder="Who it is with" /></Field>
        <Field label="Account name or ID (never a password)" hint={problem(f.account_ref)}><input className="input" maxLength={200} value={f.account_ref} onChange={(e) => setF({ ...f, account_ref: e.target.value })} autoComplete="off" /></Field>
        <Field label="Admin (who)" hint={problem(f.admin_name)}><input className="input" maxLength={200} value={f.admin_name} onChange={(e) => setF({ ...f, admin_name: e.target.value })} /></Field>
        {(f.kind === "domain" || f.renews_on) && <Field label="Renews (domains)"><input className="input" type="date" value={f.renews_on} onChange={(e) => setF({ ...f, renews_on: e.target.value })} /></Field>}
        {(f.kind === "database" || f.kind === "cloudflare" || f.kind === "other" || f.region) && (
          <Field label="Region (optional)" hint={problem(f.region)}><input className="input" maxLength={80} value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} placeholder="Sydney" /></Field>
        )}
        <Field label="Order"><input className="input" type="number" min={0} max={999} value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })} /></Field>
        <Field label="How you get into it (never a password)" wide hint={problem(f.access_note)}>
          <textarea className="input cs-textarea" rows={2} maxLength={1000} value={f.access_note} onChange={(e) => setF({ ...f, access_note: e.target.value })} placeholder="Sign in at ... with your work email. Two-step sign-in goes to ..." />
        </Field>
      </div>
      <div className="cs-row">
        <button type="submit" className="btn btn--primary btn--sm" disabled={bad}>{entry ? "Save" : "Add to the register"}</button>
        {onDone && <button type="button" className="btn btn--outline btn--sm" onClick={onDone}>Cancel</button>}
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ documents */

export function CustomerDocumentsCard({ data, act }: { data: CustomerDetail; act: Act }) {
  const [f, setF] = useState({ title: "", category: "other" as DocumentCategory, description: "", file: null as File | null, key: 0 });
  if (!__OPS_PORTAL__.customerDocuments) return null;
  const cid = data.customer.id;
  const docs = data.ownership.documents;
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!f.file) return;
    await act(async () => {
      await uploadDocument(cid, { title: f.title.trim(), category: f.category, description: f.description.trim(), file: f.file! });
      setF({ title: "", category: "other", description: "", file: null, key: f.key + 1 });
    }, "Document shared. The customer sees it under Documents.");
  }
  return (
    <section className="card" aria-labelledby="documents-title">
      <div className="card__head"><h2 className="card__title" id="documents-title">Documents</h2></div>
      <div className="card__body stack">
        <p className="muted small">Signed records, quotes, invoice records, monthly reports and handover packs show in the customer's Documents by themselves. Share anything else here: files are private, and only the customer's logins whose role fits the category can download them.</p>
        {docs.length === 0 ? (
          <p className="muted">No documents shared yet.</p>
        ) : (
          <ul className="list">
            {docs.map((d) => (
              <li key={d.id} className="list__row cs-host">
                <div className="cs-host__main">
                  <span><strong>{d.title}</strong> <span className="chip chip--outline cs-gap">{CATEGORY_LABEL[d.category].split(" (")[0]}</span></span>
                  <span className="sub">{d.file_name}, {Math.max(1, Math.round(d.size_bytes / 1024))} KB. Shared {fmtDate(d.created_at)} by {d.uploaded_by_name}</span>
                  {d.description && <span className="sub">{d.description}</span>}
                </div>
                <span className="cs-login__actions">
                  <button type="button" className="btn btn--outline btn--xs" onClick={() => act(() => openDocument(d.id, d.file_name))}>Download</button>
                  <button type="button" className="btn btn--outline btn--xs" onClick={() => window.confirm(`Remove ${d.title}? The customer will no longer see it.`) && act(() => removeDocument(cid, d.id), "Document removed.")}>Remove</button>
                </span>
              </li>
            ))}
          </ul>
        )}
        <details className="cs-details cs-details--inner">
          <summary className="cs-summary"><Icon name="plus" size={18} /> Share a document</summary>
          <form className="card__body stack" onSubmit={submit}>
            <div className="cs-grid">
              <Field label="Title (shown to the customer)"><input className="input" required minLength={2} maxLength={200} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
              <Field label="Who sees it">
                <select className="input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value as DocumentCategory })}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
                </select>
              </Field>
              <Field label="Description (optional)" wide><input className="input" maxLength={1000} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
              <Field label="File (PDF, picture, Word, Excel, CSV or text, under 20 MB)" wide>
                <input key={f.key} className="input" type="file" required accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx,.csv,.txt" onChange={(e) => setF({ ...f, file: e.target.files?.[0] ?? null })} />
              </Field>
            </div>
            <div><button type="submit" className="btn btn--primary btn--sm">Share document</button></div>
          </form>
        </details>
      </div>
    </section>
  );
}
